// @flow

import React, { useState } from 'react';
import withQueryClient from 'components/hoc/withQueryClientService';
import FocusBody from 'components/FocusBody';
import Page from 'components/Page';
import logoBlackWide from 'themes/images/logo-black-wide.png';
import FormRow from 'components/FormRow';
import * as Yup from 'yup';
import { injectIntl } from 'react-intl';
import View from 'components/View';
import { DetailBottomBarConfirm } from 'components/DetailBottomBarItem';
import DetailBottomBar, { NotificationBar } from 'components/DetailBottomBar';
import Heading from 'components/Heading';
import type { Style, Theme } from 'types';
import Text from 'components/Text';
import Version from 'components/Version';
import { Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import ValidationMessage from '../Registration/components/ValidationMessage';
import ImageBottomBar from '../Registration/components/ImageBottomBar';
import FocusBottomBar from '../Registration/components/FocusBottomBar';
import FocusContent from '../Registration/components/FocusContent';
import TextInput from '../Registration/components/TextInput';

const validateCodeMutation = gql`
  mutation recoveryCode($code: String) {
    recoveryCode(data: { record: $code, delete: false }) {
      user {
        record
        username
        name
        email
        phoneNumber
      }
    }
  }
`;

const changePasswordMutation = gql`
  mutation recoveryCode($password: String, $code: String) {
    recoveryCode(data: { password: $password, record: $code }) {
      user {
        record
        username
        name
        email
        phoneNumber
      }
    }
  }
`;

const styles = {
  page: {
    justifyContent: 'center',
    alignItems: 'center'
  },
  text: (props, { components: { FocusPage } }) => ({
    ...FocusPage.text
  }),
  img: {
    marginLeft: 20,
    height: 20
  },
  error: (props, { components: { Login } }) => ({
    ...Login.error
  }),
  header: (props, theme: Theme): Style => ({
    ...theme.components.ExternalModule.SpecificCodeHeader,
    fontSize: '18px',
    width: '420px',
    alignItems: 'flex-start'
  }),
  regCodeText: {
    padding: '15px',
    width: '420px',
    backgroundColor: '#F1F1F1'
  },
  withValidationMessage: {
    left: '120px',
    top: '-10px'
  }
};

const SendResetEmailForm = ({ intl, client, returnHome, code = null }) => {
  const [oneTimeCodeValidation, setOneTimeCodeValidation] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [passwordInputError, setPasswordInputError] = useState(false);

  const checkCodeValidity = () => {
    client
      .mutate({
        mutation: validateCodeMutation,
        variables: { code }
      })
      .then(() => {})
      .catch(() => {
        toast.error(`${intl.formatMessage({ id: 'reset-password.password.invalid-code' })}`, {
          autoClose: 30000,
          position: 'top-center'
        });

        returnHome();
      });
  };

  // for some reason useEffect was problematic,
  // that is why this workaround is used
  if (oneTimeCodeValidation) {
    checkCodeValidity();
    setOneTimeCodeValidation(false);
  }

  const validationSchema = () =>
    Yup.object().shape({
      password: Yup.string().required(intl.formatMessage({ id: 'general.required-field' })),
      confirmedPassword: Yup.string()
        .required(intl.formatMessage({ id: 'general.required-field' }))
        .oneOf(
          [Yup.ref('password'), null],
          intl.formatMessage({ id: 'reset-password.password.must-be-valid' })
        )
    });

  const validateForm = afterSuccess => {
    validationSchema()
      .isValid({
        password,
        confirmedPassword
      })
      .then(valid => {
        if (valid) {
          setPasswordInputError(false);
          afterSuccess();
        } else {
          setPasswordInputError(true);
        }
      });
  };

  const handleChange = fieldName => e => {
    const fieldValue = e.target.value;

    validationSchema()
      .isValid({
        password,
        confirmedPassword,
        [fieldName]: fieldValue
      })
      .then(valid => {
        if (fieldName === 'password') {
          setPassword(fieldValue);
        } else {
          setConfirmedPassword(fieldValue);
        }
        setPasswordInputError(!valid);
      });
  };

  const ValidationMessageRef = check =>
    check ? (
      <ValidationMessage
        message={intl.formatMessage({ id: 'reset-password.password.must-be-valid' })}
        tipPosition="left"
        style={styles.withValidationMessage}
      />
    ) : (
      <span />
    );

  const DynamicBottomBar = ({ recoveryCode, loading, error }) => (
    <View style={{ flexDirection: 'column' }}>
      {error && <NotificationBar text={intl.formatMessage({ id: 'errors.general-error' })} />}

      <FocusBottomBar>
        <ImageBottomBar>
          <img src={logoBlackWide} alt="LoyalMAN" style={styles.img} />
          <Version position="absolute" />
        </ImageBottomBar>

        <DetailBottomBar withShadow="false">
          <DetailBottomBarConfirm
            name={
              loading
                ? intl.formatMessage({ id: 'general.sending' })
                : intl.formatMessage({ id: 'general.send' })
            }
            onClick={() => {
              validateForm(() => {
                recoveryCode().then(() => {
                  toast.success(intl.formatMessage({ id: 'reset-password.password.changed' }), {
                    autoClose: 30000,
                    position: 'top-center'
                  });
                  setPassword('');
                  setConfirmedPassword('');
                  returnHome();
                });
              });
            }}
          />
        </DetailBottomBar>
      </FocusBottomBar>
    </View>
  );

  return (
    <Page title={intl.formatMessage({ id: 'reset-password.page-title' })}>
      <View isExpanded style={styles.page}>
        <View style={{ width: 500 }}>
          <FocusContent>
            <FocusBody>
              <View
                isExpanded
                isVertical
                style={{ padding: 40, paddingBottom: 30, alignItems: 'center', width: '100%' }}>
                <FormRow>
                  <Heading style={styles.header} scale={1.5}>
                    {intl.formatMessage({ id: 'reset-password.heading' })}
                  </Heading>
                </FormRow>
                <FormRow>
                  <Text style={styles.regCodeText}>
                    {intl.formatMessage({ id: 'reset-password.text' })}
                  </Text>
                </FormRow>
                <FormRow>
                  {ValidationMessageRef(passwordInputError)}
                  <TextInput
                    hasError={passwordInputError}
                    label={intl.formatMessage({ id: 'reset-password.new-password' })}
                    value={password}
                    styleInput={{ width: '420px' }}
                    styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    onChange={handleChange('password')}
                  />
                </FormRow>
                <FormRow>
                  <TextInput
                    hasError={passwordInputError}
                    label={intl.formatMessage({ id: 'reset-password.password.confirmation' })}
                    value={confirmedPassword}
                    styleInput={{ width: '420px' }}
                    styleBox={{ flexDirection: 'column', alignItems: 'flex-start' }}
                    onChange={handleChange('confirmedPassword')}
                  />
                </FormRow>
              </View>

              <Mutation
                mutation={changePasswordMutation}
                variables={{ password, code }}
                onCompleted={() => {}}>
                {(recoveryCode, { error, loading }) => (
                  <DynamicBottomBar recoveryCode={recoveryCode} loading={loading} error={error} />
                )}
              </Mutation>
            </FocusBody>
          </FocusContent>
        </View>
      </View>
    </Page>
  );
};

export default injectIntl(withQueryClient(SendResetEmailForm));
